import type { WorkspaceType } from '@/features/workspaces/constants';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import { ClauseCategoryStatus } from '@/features/clauses/constants';
import type {
  ClauseSubcategoryClause,
  GetClauseCategoriesResponse,
} from '@/features/clauses/types';
import { resourceLatestVersionQuery } from '@/features/workspaces/queries';
import { parseStreamRow, queryStream, type StreamRowPayload } from '@/features/workspaces/stream';

export const CLAUSE_CATEGORIES_QUERY_KEY = 'clauseCategories' as const;

export const clauseCategoriesQuery = (type: WorkspaceType, resourceId: string) => {
  const docType = DOCUMENT_TYPE[type];
  const queryKey = [CLAUSE_CATEGORIES_QUERY_KEY, type, resourceId] as const;
  return queryOptions({
    queryKey,
    queryFn: async () => {
      const response = await api.get<GetClauseCategoriesResponse>(
        'v1/analysis/clause_scanner/categories',
        {
          params: { type: docType, resourceId },
          validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        },
      );
      return response.status === 404 ? null : response.data;
    },
    meta: {
      stream: queryStream<
        GetClauseCategoriesResponse | null,
        StreamRowPayload<ClauseCategoryStatus> & { op?: string }
      >({
        topic: 'clause_scanner',
        type: docType,
        resourceId,
        versioned: true,
        isActive: (data) =>
          data?.categories.some((category) => category.status !== ClauseCategoryStatus.DONE) ??
          false,
        onStatusUpdate: (payload, queryClient) => {
          const invalidate = () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries(resourceLatestVersionQuery(resourceId));
          };
          const current = queryClient.getQueryData<GetClauseCategoriesResponse | null>(queryKey);
          if (
            current === null &&
            'rows' in payload &&
            Array.isArray(payload.rows) &&
            payload.rows.length === 0
          )
            return;

          if (payload.op === 'delete') {
            if (!('id' in payload) || !payload.id) return invalidate();
            const deletedId = payload.id;
            return queryClient.setQueryData<GetClauseCategoriesResponse | null>(
              queryKey,
              (old) =>
                old && {
                  ...old,
                  categories: old.categories.filter((category) => category.id !== deletedId),
                },
            );
          }

          const row = parseStreamRow(payload);
          if (row === 'refetch') return invalidate();
          if (!row) return;

          const known = current?.categories.some((category) => category.id === row.rowId);
          if (!known || row.status !== ClauseCategoryStatus.IN_PROGRESS) return invalidate();

          queryClient.setQueryData<GetClauseCategoriesResponse | null>(
            queryKey,
            (old) =>
              old && {
                ...old,
                categories: old.categories.map((category) =>
                  category.id === row.rowId
                    ? { ...category, status: row.status, progress: row.progress }
                    : category,
                ),
              },
          );
        },
      }),
    },
  });
};

export const CLAUSE_SUBCATEGORY_QUERY_KEY = 'clauseSubcategory' as const;

export const clauseSubcategoryQuery = (
  type: WorkspaceType,
  resourceId: string,
  subCategoryId: string,
) => {
  const docType = DOCUMENT_TYPE[type];
  return queryOptions({
    queryKey: [CLAUSE_SUBCATEGORY_QUERY_KEY, type, resourceId, subCategoryId],
    queryFn: async () =>
      (
        await api.get<{
          name: string;
          clauses: ClauseSubcategoryClause[];
        }>(`v1/analysis/clause_scanner/subcategories/${subCategoryId}`, {
          params: { type: docType, resourceId },
        })
      ).data,
  });
};
